import IncorrectCircle from '@ingka/ssr-icon/paths/incorrect';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { CommunicationSizeComponent, ComponentIds, Entity } from '@inter-ikea-core/core';
import { Product, ProductType } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { showInfoMessage } from 'ducks/layout';
import {
  getMarketProducts,
  getProductsForGroup,
  getSelectedGroupId,
  getToggleState,
  toggleApplied
} from 'ducks/products';
import { Dimension } from '@inter-ikea-storageone/range';
import {
  getMatchingDoorsWithHandles,
  getDoorsFromSelectedEntity
} from '../../utils/changeSize/doorUtils';
import SizeToggle from 'ui/SizeToggle/SizeToggle';
import { getFilterOptions, getFilterParams, getUnit } from 'filters/SizeFilter/utils';
import * as R from 'ramda';
import { FilterType } from 'rangeConfig/@types';
import { CheckWidget } from 'rangeConfig/shared/widgets/checkWidget';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import media from 'ui/media';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import { getFrameSizesInScene, getHeightOfLightsInScene } from '../../utils/changeSize/frameUtils';
import { useTranslations } from 'hooks/useTranslations';
import { useBehaviorTracker } from 'contexts/behaviors';
import { useServiceSettings } from 'contexts/serviceSettings';

const Wrapper = styled.div`
  padding-top: ${({ theme }) => theme.space.l};
  ${media.mobile`
    padding: 1rem 0;
  `}
`;

type SizeToggleProps = CheckWidget & {
  dimensionType: Dimension;
  onChangeSize: (type: Dimension, size: number, products: Product[]) => void;
  showTitle: boolean;
  defaultSize?: number;
};

const DEFAULT_HEIGHT_MARGIN: number = 10;
const SLIDING_DOOR_HEIGHT_MARGIN: number = 40;

const SolutionSizeToggle = ({
  dimensionType,
  onChangeSize,
  defaultSize,
  hideTab
}: SizeToggleProps) => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const behaviorTracker = useBehaviorTracker();
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();

  const allProducts = useSelector(getMarketProducts);
  const products = useSelector(getProductsForGroup);
  const toggleState = useSelector(getToggleState);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const { formatMessage } = useIntl();

  const sizeProducts = products.filter(
    product => product.type !== ProductType.PAX_CORNER_SOLUTION_ASM
  );
  const availableSizes = getFilterOptions(dimensionType)(sizeProducts);
  const unit = getUnit(sizeProducts);
  const sizes = Object.entries(availableSizes).map(([size, communicated]) => {
    const value = parseInt(size);
    return { value: value, text: communicated, unit: unit };
  });

  const defaultSizeValue =
    sizes.find(size => size.value === defaultSize)?.value || R.last(sizes)?.value;
  const filterState = toggleState(selectedGroupId, dimensionType);
  const sizesInScene = getFrameSizesInScene(dimensionType, coreApi);
  const communicatedSelectedValue = Object.entries(availableSizes)
    .map(([size, communicated]) => {
      return {
        size,
        communicated
      };
    })
    .find(val => filterState.selected === parseInt(val.size))?.communicated;
  const [mixedSizes, setMixedSizes] = useState(sizesInScene.length > 1);
  const [disabledToggle, setDisabledToggle] = useState(false);
  const [showInlineMessage, setShowInlineMessage] = useState(false);

  useEffect(() => {
    if (showInlineMessage) {
      dispatch(
        showInfoMessage({
          message: 'storageone.changeSizeMissingProductsErrorBody',
          params: { value: communicatedSelectedValue, unit: unit }
        })
      );
    }
    // We don't want to listen for communicatedSelectedValue and unit, since it will cause to
    // message will update when frames in scene change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showInlineMessage]);

  const applySizeFilter = useCallback(
    (size: number) => {
      const { productIds, newFilterState } = getFilterParams(
        FilterType.TOGGLE,
        size,
        dimensionType,
        products,
        allProducts,
        filterState
      );
      if (newFilterState.selected !== filterState.selected) {
        dispatch(
          toggleApplied({
            id: dimensionType,
            productGroupId: selectedGroupId,
            productIdList: productIds,
            filterState: newFilterState
          })
        );
      }
    },
    [allProducts, dispatch, filterState, products, selectedGroupId, dimensionType]
  );

  const removeSizeFilter = useCallback(() => {
    if (filterState.selected === undefined || filterState === null) return;
    const productsIdList = products.map(p => p.coreProductId);
    dispatch(
      toggleApplied({
        id: dimensionType,
        productGroupId: selectedGroupId,
        productIdList: productsIdList,
        filterState: {}
      })
    );
  }, [dispatch, filterState, products, selectedGroupId, dimensionType]);

  const checkNewSolution = (oldFrames: Entity[]) => {
    const newFrames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
    let nbrOfUnchangedFrames = 0;
    newFrames.forEach(frame => {
      if (oldFrames.includes(frame)) {
        nbrOfUnchangedFrames += 1;
      }
    });
    if (nbrOfUnchangedFrames === newFrames.length) {
      dispatch(showInfoMessage({ message: 'storageone.changeSizeMissingFramesMessage' }));
    } else if (nbrOfUnchangedFrames > 0) {
      dispatch(showInfoMessage({ message: 'storageone.changeSizeSomeMissingFramesMessage' }));
    }
  };

  const onSizeChanged = (size: number) => {
    const meta = { size: { value: size, dimension: dimensionType } };

    behaviorTracker.withMeta(meta).custom(async () => {
      if (size === filterState.selected) return;

      checkAvailableDoors();

      if (sizesInScene.length) {
        await coreApi.performLongCoreOperation(() => {
          const oldFramesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
          coreApi.validations['WallVisibilityValidation']?.disable();
          coreApi.validations['SeeThroughFramesValidation']?.disable();
          onChangeSize(dimensionType, size, allProducts);
          coreApi.validations['WallVisibilityValidation']?.enable();
          coreApi.validations['SeeThroughFramesValidation']?.enable();
          checkNewSolution(oldFramesInScene);
        });
      } else {
        applySizeFilter(size);
      }
    });
  };

  const checkAvailableDoors = () => {
    if (dimensionType !== 'height') return;
    const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
    const frameWithNoAvailableDoor = framesInScene.find(frame => {
      const previousDoors = getDoorsFromSelectedEntity(coreApi, frame, allProducts);
      const doorsMatchingCurrentDoorsWithHandles = getMatchingDoorsWithHandles(
        previousDoors,
        allProducts,
        coreApi,
        dimensionType
      );
      if (
        previousDoors &&
        previousDoors.length > 0 &&
        doorsMatchingCurrentDoorsWithHandles.length === 0
      ) {
        return true;
      }
      return false;
    });
    if (frameWithNoAvailableDoor) {
      setShowInlineMessage(true);
    } else {
      setShowInlineMessage(false);
    }
  };

  const checkSizesInScene = useCallback(() => {
    const sizesInScene = getFrameSizesInScene(dimensionType, coreApi);

    if (sizesInScene.length > 1) {
      setMixedSizes(true);
      removeSizeFilter();
      return;
    }
    if (sizesInScene.length > 0 && (sizesInScene[0] !== filterState.selected || mixedSizes)) {
      applySizeFilter(sizesInScene[0]);
    }
    setMixedSizes(false);
  }, [applySizeFilter, coreApi, filterState.selected, mixedSizes, removeSizeFilter, dimensionType]);

  useEffect(() => {
    return coreApi.subscribe('history', checkSizesInScene);
  });

  useEffect(() => {
    checkSizesInScene();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sizesInScene = getFrameSizesInScene(dimensionType, coreApi);
    if (
      defaultSizeValue &&
      (filterState.selected === undefined || filterState === null) &&
      sizesInScene.length === 0
    ) {
      applySizeFilter(defaultSizeValue);
    }

    const checkRoomHeight = () => {
      if (
        dimensionType !== 'height' ||
        (sizesInScene.length > 0 && filterState.selected === defaultSizeValue)
      )
        return;
      const roomSize = coreApi.kvadratRoom?.getDimensions();
      const lightHeight = getHeightOfLightsInScene(allProducts, coreApi);
      const tallestFrameSizeOption = Math.max(...sizes.map(s => s.value));

      if (roomSize && tallestFrameSizeOption && roomSize.y < tallestFrameSizeOption + lightHeight) {
        if (!disabledToggle) {
          setDisabledToggle(true);
          if (sizes.length > 0 && filterState.selected === tallestFrameSizeOption) {
            applySizeFilter(sizes[0].value);
          }
        }
      } else {
        if (disabledToggle !== false) setDisabledToggle(false);
      }
    };

    checkRoomHeight();
  }, [
    applySizeFilter,
    defaultSizeValue,
    defaultSize,
    filterState,
    mixedSizes,
    checkSizesInScene,
    dimensionType,
    sizesInScene.length,
    coreApi,
    allProducts,
    sizes,
    disabledToggle
  ]);

  useEffect(() => {
    hideTab && hideTab(sizes.length < 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizes]);

  if (sizes.length < 2) {
    return null;
  }

  const getFrameHeight = (frame: Entity) => {
    return (
      frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size.height || 0
    );
  };

  const getMaxFrameHeight = (frames: Entity[]) => {
    return Math.max(...frames.map(frame => getFrameHeight(frame)));
  };

  const frames = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  let maxFrameHeight = getMaxFrameHeight(frames);

  if (frames.length < 1) {
    maxFrameHeight = filterState.selected;
  }

  const highestFrames = frames.filter(frame => getFrameHeight(frame) === maxFrameHeight);

  const hasSlidingDoor =
    highestFrames.find(frame =>
      coreApi.entities.findEntityInHierarchyWithOneOfProductTypes(frame, [
        ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
        ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
      ])
    ) !== undefined;

  const localizedHeight = disabledToggle
    ? adjustMeasurementsSystem(maxFrameHeight, translations, useMetric)
    : hasSlidingDoor
    ? adjustMeasurementsSystem(maxFrameHeight + SLIDING_DOOR_HEIGHT_MARGIN, translations, useMetric)
    : adjustMeasurementsSystem(maxFrameHeight + DEFAULT_HEIGHT_MARGIN, translations, useMetric);

  const sheetMessage = {
    message: disabledToggle
      ? 'storageone.disabledCeilingHeightInfo'
      : 'storageone.minimumCeilingHeightSheetText2',
    params: { height: localizedHeight, unit }
  };

  const showMinInfoMessage = dimensionType === 'height' || disabledToggle;

  return (
    <>
      <SizeToggle
        mixedSizes={mixedSizes}
        currentSize={filterState.selected}
        sizes={sizes}
        disabled={disabledToggle}
        onChangeSize={onSizeChanged}
      />
      {showMinInfoMessage && (
        <>
          <Wrapper aria-live="polite" aria-atomic="true">
            <SkapaInlineMessage
              body={formatMessage({ id: sheetMessage.message }, sheetMessage.params)}
              variant={
                disabledToggle
                  ? InlineMessageVariantEnum.NEGATIVE
                  : InlineMessageVariantEnum.INFORMATIVE
              }
              ssrIcon={disabledToggle ? IncorrectCircle : InformationCircle}
              subtle
            />
          </Wrapper>
        </>
      )}
    </>
  );
};

export default SolutionSizeToggle;
